import Immutable from 'immutable'
import { createSelector } from 'reselect'

import createClient from '../../records/Client'
import createListClient from '../../records/ListClient'
import { makeSelectAuthenticatedClientIds } from './authentication'

// ------------------------------------
// Constants
// ------------------------------------
export const LOAD_CLIENT_LIST = 'inf/client/LOAD_CLIENT_LIST'
export const LOAD_CLIENTS = 'inf/client/LOAD_CLIENTS'
export const RECEIVE_CLIENT_LIST = 'inf/client/RECEIVE_CLIENT_LIST'
export const RECEIVE_CLIENTS = 'inf/client/RECEIVE_CLIENTS'
export const RECEIVE_CLIENTS_SEARCH = 'inf/client/RECEIVE_CLIENTS_SEARCH'
export const SEARCH_CLIENTS = 'inf/client/SEARCH_CLIENTS'

export const constants = {
  LOAD_CLIENT_LIST,
  LOAD_CLIENTS,
  RECEIVE_CLIENT_LIST,
  RECEIVE_CLIENTS,
  RECEIVE_CLIENTS_SEARCH,
  SEARCH_CLIENTS,
}

// ------------------------------------
// Actions
// ------------------------------------
export const loadClientList = () => ({
  type: LOAD_CLIENT_LIST,
})
export const loadClients = clientIds => ({
  payload: { clientIds },
  type: LOAD_CLIENTS,
})
export const receiveClientList = clients => ({
  payload: { clients },
  type: RECEIVE_CLIENT_LIST,
})
export const receiveClients = clients => ({
  payload: { clients },
  type: RECEIVE_CLIENTS,
})
export const receiveClientsSearch = (query, clients) => ({
  payload: {
    clients,
    query,
  },
  type: RECEIVE_CLIENTS_SEARCH,
})
export const searchClients = query => ({
  payload: { query },
  type: SEARCH_CLIENTS,
})

export const actions = {
  loadClientList,
  loadClients,
  receiveClientList,
  receiveClients,
  receiveClientsSearch,
  searchClients,
}

// ------------------------------------
// Selectors
// ------------------------------------
const getClients = state => state.client.get('clients')
const getSearchMap = state => state.client.get('searchMap')

export const makeSelectClient = () => createSelector(
  getClients,
  (state, { clientId }) => clientId,
  (clients, clientId) => clients.find(search => search.id === clientId),
)

export const makeSelectClients = () => createSelector(
  getClients,
  (state, { clientIds }) => clientIds,
  (clients, clientIds) => clients.filter(search => clientIds.indexOf(search.id) !== -1),
)

export const makeSelectClientSearch = () => createSelector(
  getClients,
  getSearchMap,
  (state, { query }) => query,
  (state, { selected }) => selected,
  (clients, searchMap, query, selected) => {
    let ids
    if (!query || !searchMap.has(query)) {
      ids = Immutable.OrderedSet()
    } else {
      ids = searchMap.get(query)
    }

    if (selected) {
      ids = ids.add(selected)
    }

    return ids.map(id => clients.find(search => search.id === id))
  },
)

export const makeSelectClientsForPartner = () => createSelector(
  getClients,
  (state, { partner }) => partner,
  (clients, partner) => partner.relationships
    .map(rel => clients.find(search => search.id === rel.clientId))
    .sortBy(client => client.name),
)

export const makeSelectIpns = () => createSelector(
  getClients,
  makeSelectAuthenticatedClientIds(),
  (clients, clientIds) => clients
      .filter(c => clientIds.has(c.id))
      .reduce((acc, client) => acc.union(client.ipns), Immutable.Set()),
)

export const selectClient = createSelector(
  state => state.get('clients'),
  (state, clientId) => clientId,
  (clients, clientId) => clients.find(c => c.get('id') === clientId),
)

export const selectClients = createSelector(
  state => state.get('clients'),
  (state, clientIds) => (Immutable.Iterable.isIterable(clientIds) ? clientIds : Immutable.fromJS(clientIds)),
  (clients, clientIds) => clients.filter(c => clientIds.includes(c.get('id'))),
)

export const selectClientsBillingGroups = createSelector(
  clients => clients.sortBy(c => c.get('name')),
  (clients, billingGroups) => billingGroups.sortBy(bg => bg.get('name')),
  (clients, billingGroups) => clients.reduce(
    (result, client) => {
      const clientGroups = billingGroups.filter(group => client.get('billing_groups').includes(group.get('id')))

      return result.set(client, clientGroups)
    },
    Immutable.OrderedMap(),
  ),
)

export const selectors = {
  selectClient,
  selectClients,
  selectClientsBillingGroups,
}

// ------------------------------------
// Reducer
// ------------------------------------
export const initialState = Immutable.fromJS({
  clients: Immutable.Set(),
  list: Immutable.OrderedSet(),
  searchMap: Immutable.Map(),
})

function replaceClient(clients, rawClient) {
  const newClient = createClient(rawClient)

  return clients
    .filter(client => client.id !== newClient.id)
    .add(newClient)
}

export default function reducer(state = initialState, action) {
  const { payload } = action

  switch (action.type) {
    case RECEIVE_CLIENT_LIST: {
      const clientList = payload.clients.reduce((list, client) =>
          list.add(createListClient(client))
        , Immutable.OrderedSet())

      return state.set('list', clientList)
    }

    case RECEIVE_CLIENTS:
      return payload.clients.reduce((newState, rawClient) => newState
          .set('clients', replaceClient(newState.get('clients'), rawClient))
      , state)

    case RECEIVE_CLIENTS_SEARCH: {
      return payload.clients.reduce((newState, rawClient) => {
        const currentSearchMap = newState.getIn(['searchMap', payload.query], Immutable.OrderedSet())

        return newState
          .setIn(['searchMap', payload.query], currentSearchMap.add(rawClient.id))
      }, state)
    }


    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./deps/internal-components/src/redux/modules/client.js